import { useState } from "react";
import { HelpCircle, ChevronDown } from "lucide-react";

/**
 * Render seguro de texto con markup mínimo (b, i, code) sin usar
 * `dangerouslySetInnerHTML`. Convertimos el string en un array de nodos React
 * usando una regex whitelist. Cualquier tag desconocido se muestra como texto.
 */
function renderInline(text) {
  if (!text) return null;
  const nodes = [];
  const re = /<(b|i|code)>([\s\S]*?)<\/\1>/gi;
  let lastIndex = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      nodes.push(text.slice(lastIndex, m.index));
    }
    const tag = m[1].toLowerCase();
    const inner = m[2];
    if (tag === "b") nodes.push(<b key={`n-${i++}`}>{inner}</b>);
    else if (tag === "i") nodes.push(<i key={`n-${i++}`}>{inner}</i>);
    else if (tag === "code") nodes.push(<code key={`n-${i++}`} className="px-1 py-0.5 rounded bg-muted text-[12px] font-mono">{inner}</code>);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/**
 * Panel colapsable con instrucciones breves de "cómo usar esta pantalla".
 * Se guarda el estado abierto/cerrado en localStorage por key para no molestar.
 */
export default function UsageTip({ storageKey, title = "Cómo usar esta pantalla", steps = [], defaultOpen = false }) {
  const key = `usage_tip_${storageKey}`;
  const [open, setOpen] = useState(() => {
    const v = typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
    if (v === null) return defaultOpen;
    return v === "1";
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { window.localStorage.setItem(key, next ? "1" : "0"); }
    catch (e) { console.warn("UsageTip: no se pudo persistir en localStorage", e); }
  };

  return (
    <div className={`mb-6 rounded-xl border transition-colors ${open ? "border-primary/20 bg-accent/60" : "border-border bg-card"}`} data-testid={`usage-tip-${storageKey}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`usage-tip-toggle-${storageKey}`}>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${open ? "bg-emerald-100 text-primary" : "bg-muted text-muted-foreground"}`}>
            <HelpCircle className="w-4 h-4" />
          </div>
          <div>
            <div className={`text-sm font-semibold ${open ? "text-primary" : "text-foreground/80"}`}>{title}</div>
            {!open && <div className="text-xs text-muted-foreground">Toca para ver los pasos rápidos</div>}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground/70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          <ol className="space-y-2 text-sm">
            {steps.map((s, i) => (
              <li key={`step-${i}-${(s || "").slice(0, 20)}`} className="flex gap-3 items-start">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</div>
                <div className="text-foreground/80">{renderInline(s)}</div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
